import { useEffect, useState } from 'react';

const PARTICLES = Array.from({ length: 22 }, (_, i) => ({
  id: i,
  left: Math.random() * 100,
  top: Math.random() * 100,
  size: Math.random() * 4 + 1,
  delay: Math.random() * 5,
  duration: Math.random() * 8 + 6,
}));

export default function Hero() {
  const [loaded, setLoaded] = useState(false);
  const [lineReady, setLineReady] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setLoaded(true), 100);
    const t2 = setTimeout(() => setLineReady(true), 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0A1628]"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/37ce03366ef1d2082013b355002186b6.jpg"
          alt="Quantum Electrical Services background"
          className="w-full h-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628] via-[#0A1628]/85 to-[#0A1628]/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-transparent to-[#0A1628]/60" />
      </div>

      {/* Gold particles */}
      {PARTICLES.map((p) => (
        <span
          key={p.id}
          className="absolute rounded-full bg-[#C8A96A] pointer-events-none"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: p.size,
            height: p.size,
            opacity: 0.3,
            animation: `floatParticle ${p.duration}s ${p.delay}s ease-in-out infinite alternate`,
          }}
        />
      ))}

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pt-24 md:pt-28 pb-16 flex flex-col lg:flex-row items-center gap-10 lg:gap-12">
        {/* Left text */}
        <div className="flex-1 text-center lg:text-left w-full">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 border border-[#C8A96A]/40 rounded-full px-4 py-1.5 mb-6 transition-all duration-700 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-[#C8A96A] animate-pulse" />
            <span className="text-[#C8A96A] text-xs tracking-widest uppercase font-medium">
              Sleaford&apos;s Premier Electrician
            </span>
          </div>

          {/* H1 with line reveal animation - targeting electrician Sleaford */}
          <h1 className="font-playfair leading-tight mb-6">
            {/* Line 1 */}
            <span
              className={`block text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl transition-all duration-700 delay-100 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              Quantum
            </span>

            {/* Line 2 - gold shimmer with line reveal */}
            <span className="block relative overflow-hidden">
              <span
                className={`block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold transition-all duration-700 delay-200 ${
                  loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{
                  backgroundImage: 'linear-gradient(90deg, #C8A96A, #E0C78A, #C8A96A, #A8894F)',
                  backgroundSize: '300% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: loaded ? 'goldShimmer 4s ease-in-out infinite' : 'none',
                }}
              >
                Electrical
              </span>
              {/* Gold sweep line reveal */}
              <span
                className="absolute bottom-0 left-0 h-0.5 rounded-full transition-all duration-1000 delay-500"
                style={{
                  width: lineReady ? '100%' : '0%',
                  background: 'linear-gradient(90deg, #A8894F, #E0C78A, #C8A96A)',
                  boxShadow: '0 0 8px rgba(200,169,106,0.8)',
                }}
              />
            </span>

            {/* Line 3 - electrician Sleaford (SEO target) */}
            <span
              className={`block text-white text-2xl sm:text-3xl md:text-4xl font-medium tracking-wide transition-all duration-700 delay-300 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              Electrician{' '}
              <span className="text-[#C8A96A]/80">Sleaford</span>
            </span>
          </h1>

          <p
            className={`text-white/70 text-base md:text-lg leading-relaxed max-w-xl mb-8 mx-auto lg:mx-0 transition-all duration-700 delay-400 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            NAPIT registered, fully qualified electricians serving Sleaford &amp; Lincolnshire. 
            From full rewires to EV chargers — quality work, transparent pricing, zero hassle.
          </p>

          <div
            className={`flex flex-col sm:flex-row gap-4 justify-center lg:justify-start transition-all duration-700 delay-500 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <a
              href="tel:01529265060"
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#C8A96A] to-[#A8894F] hover:from-[#E0C78A] hover:to-[#C8A96A] text-black font-bold px-8 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
            >
              <i className="ri-phone-fill text-lg" />
              Call 01529 265060
            </a>
            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="flex items-center justify-center gap-2 border-2 border-[#C8A96A] text-[#C8A96A] hover:bg-[#C8A96A]/10 font-bold px-8 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-list-3-line text-lg" />
              Free Quote
            </button>
          </div>

          {/* Trust badges */}
          <div
            className={`flex flex-wrap gap-4 md:gap-6 mt-10 justify-center lg:justify-start transition-all duration-700 delay-700 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            {[
              { icon: 'ri-shield-check-fill', label: 'NAPIT Registered' },
              { icon: 'ri-star-fill', label: '5★ Reviews' },
              { icon: 'ri-map-pin-2-fill', label: 'Sleaford Based' },
            ].map(({ icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className={`${icon} text-[#C8A96A] text-lg`} />
                </div>
                <span className="text-white/70 text-sm">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right logo glow — hidden on small screens */}
        <div
          className={`hidden md:flex flex-shrink-0 transition-all duration-1000 delay-600 ${
            loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          }`}
        >
          <div className="relative w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full border border-[#C8A96A]/20 animate-ping" style={{ animationDuration: '3s' }} />
            <div className="absolute inset-4 rounded-full border border-[#C8A96A]/30" style={{ animation: 'slowSpin 20s linear infinite' }} />
            <div className="absolute inset-8 rounded-full border border-[#E0C78A]/20" style={{ animation: 'slowSpinReverse 15s linear infinite' }} />
            <div className="absolute inset-12 rounded-full bg-[#C8A96A]/10 blur-2xl" />
            <div
              className="absolute inset-16 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(200,169,106,0.15) 0%, transparent 70%)', animation: 'pulseGlow 3s ease-in-out infinite' }}
            />
            <img
              src="https://storage.readdy-site.link/project_files/00423dcc-69f9-4748-ac0c-c15d54e5586b/90758309-4e57-4704-ada6-1299ec143e1f_3eb45589-c339-4fd1-9e2d-32cca06dacf9-removebg-preview.png?v=2a77e748ecd680d6c594c6350bb285eb"
              alt="Quantum Electrical Services logo"
              className="relative z-10 w-44 h-44 md:w-52 md:h-52 lg:w-64 lg:h-64 object-contain"
              style={{ filter: 'drop-shadow(0 0 40px rgba(200,169,106,0.7)) drop-shadow(0 0 80px rgba(200,169,106,0.3))' }}
            />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#C8A96A]/60 hover:text-[#C8A96A] transition-colors cursor-pointer"
      >
        <span className="text-xs tracking-widest uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-xl" style={{ animation: 'bounce 2s ease-in-out infinite' }} />
      </button>
    </section>
  );
}
